import React from "react";
import {useEffect, useState} from "react";
import ShowProgressSpinner from "../../../common/ui/show-progress-spinner";
import {DESIGN_CONTENT_INPUT_FIELDS, DESIGN_VARIANT_FIELDS_LIST} from "../../constants/design.constant";
import ShowEmptyListMessage from "../common/show-empty-list-message";
import ShowDesignVariantStatus from "../common/show-design-variant-status";
import DesignDetailTable from "./design-detail-table";
import {prepareDesignTags} from "../../utils/design-tag-util";
import _ from "lodash";
import {updateSingleDesign} from "../../services/design-data-service";
import CopyDataToClipboard from "../../../common/ui/copy-data-to-clipboard";
import {prepareProductDescriptionMetaPromptMessages} from "../../utils/open-ai-prompt-util";
import {generateJsonContent} from "../../../common/services/open-ai-service";
import RenderFields from "../../../common/ui/render-fields";
import {COLLECTION_INPUT_FIELDS} from "../../../shopify/constants/shopify-blog.constant";
import {updateDesignVariants, updateSingleDesignVariant} from "../../services/design-variant-data-service";
import {processDesignVariantStatistics} from "../../utils/design-util";
import {prepareDesignVariantLabel} from "../../utils/design-variant-util";

const defaultFilterCriteria: any = {
    toneOfVoice: 'Catchy',
    count: '',
}

const DesignSummary = ({designDetail, setDesignDetail, allTags, parentProduct, quoteDetail}) => {
    const [initialStep, setInitialStep] = useState(true);
    const [dataFetchInProgress, setDataFetchInProgress] = useState(true);
    const [statusChangeInProgress, setStatusChangeInProgress] = useState(false);
    const [designTagList, setDesignTagList] = useState(null);
    const [contentDetail, setContentDetail] = useState(defaultFilterCriteria);
    const [errorMessage, setErrorMessage] = useState(null);

    useEffect(() => {
        if (initialStep && designDetail && allTags && parentProduct && quoteDetail) {
            processDesignTags();
            setInitialStep(false);
            setDataFetchInProgress(false);
        }
    }, [initialStep, allTags, designDetail, parentProduct, quoteDetail]);

    const processDesignTags = async () => {
        const {designTagDetailList} = prepareDesignTags(allTags, designDetail, parentProduct, quoteDetail, null, null);
        const existingDesignDetail = {...designDetail};
        existingDesignDetail.designTags = _.compact(_.uniq(_.map(designTagDetailList, 'startText')));

        _.forEach(existingDesignDetail.variants, (variant) => {
            prepareDesignVariantLabel(variant, designDetail);
        });

        setDesignDetail(existingDesignDetail);
        setDesignTagList(designTagDetailList);
        await updateSingleDesign(existingDesignDetail);

        if (designDetail?.content?.toneOfVoice) {
            setContentDetail({
                toneOfVoice: designDetail?.content?.toneOfVoice
            });
        }

        // console.log('designDetail', designDetail);
    }

    const handleContentDetailChanges = async (event) => {
        const {name, value, type} = event.target;
        const existingContentDetail = {...contentDetail};

        if (type === 'checkbox') {
            existingContentDetail[name] = !existingContentDetail[name];
        } else if (type === 'number') {
            existingContentDetail[name] = _.parseInt(value);
        } else {
            existingContentDetail[name] = value;
        }

        setContentDetail(existingContentDetail);
    }

    const generateProductDescriptions = async (stopNext?, currentDesignDetail?) => {
        setDataFetchInProgress(true);
        const existingContentDetail = {...contentDetail};
        let existingDesignDetail = currentDesignDetail;

        if (!currentDesignDetail) {
            existingDesignDetail = {...designDetail};
        }

        if (!existingContentDetail?.count && existingDesignDetail?.variants?.length > 6) {
            let limit = 6;
            existingContentDetail.count = _.parseInt(String(existingDesignDetail?.variants?.length / 2));

            if (existingContentDetail.count > limit) {
                existingContentDetail.count = limit;
            }
        }

        const promptMessages = prepareProductDescriptionMetaPromptMessages(existingDesignDetail, existingContentDetail);
        let contentList: any = await generateJsonContent(promptMessages);

        if (contentList) {
            setErrorMessage('');

            if (existingDesignDetail?.content?.contentList?.length > 0) {
                contentList = _.concat(existingDesignDetail?.content?.contentList, contentList);
            }

            existingDesignDetail.content = {
                toneOfVoice: existingContentDetail?.toneOfVoice,
                contentList: contentList
            };

            setDesignDetail(existingDesignDetail);
            await updateSingleDesign(existingDesignDetail);
        } else {
            setErrorMessage('Generation Failed');
        }

        setDataFetchInProgress(false);

        if (!stopNext) {
            if (existingDesignDetail?.content?.contentList?.length < existingDesignDetail?.variants?.length) {
                let limit = 6;
                existingContentDetail.count = existingDesignDetail?.variants?.length - existingDesignDetail?.content?.contentList?.length;

                if (existingContentDetail.count > limit) {
                    existingContentDetail.count = limit;
                }

                setContentDetail(existingContentDetail);

                setTimeout(async () => {
                    await generateProductDescriptions(true, existingDesignDetail);
                }, 10);
            }
        }
    }

    const markAsPublishedForAllVariants = async () => {
        setStatusChangeInProgress(true);

        setTimeout(async () => {
            const existingDesignDetail = {...designDetail};
            const existingVariants = existingDesignDetail.variants;
            const designVariants = [];

            _.forEach(existingVariants, (variant) => {
                variant.status = 'published';

                designVariants.push({
                    id: variant.id,
                    status: variant.status
                });
            });

            existingDesignDetail.variants = existingVariants;
            processDesignVariantStatistics(existingDesignDetail);

            setDesignDetail(existingDesignDetail);

            await updateDesignVariants(designVariants);
            await updateSingleDesign(existingDesignDetail);

            setStatusChangeInProgress(false);
        }, 10);
    }

    return (
        <>
            <div className="flex justify-start w-full">
                <div className="w-full flex justify-between">

                    <div className="flex justify-start items-center w-1/2">
                        <div className="flex items-center text-lg font-bold text-gray-500">
                            Design Summary
                        </div>

                        <div>
                            <button
                                className={`py-2 px-4 button-purple ml-4`}
                                onClick={markAsPublishedForAllVariants}>
                                <span>Mark All As Published</span>
                            </button>
                        </div>
                    </div>

                    <div className={'flex justify-center items-center'}>
                        <ShowProgressSpinner
                            dataFetchInProgress={dataFetchInProgress || statusChangeInProgress}/>

                        <div
                            className={`${designDetail?.variants?.length === designDetail?.content?.contentList?.length ? 'text-purple-500' : 'text-red-500'} text-lg font-bold ml-1 flex justify-center items-center`}>
                            <span className={'text-center'}>{designDetail?.variants?.length} Variants</span>
                            <span className={'mx-1'}>|</span>
                            <span
                                className={'text-center'}> {designDetail?.content?.contentList?.length || 0} Contents</span>
                        </div>

                        {errorMessage &&
                            <div className={'text-lg text-red-500 font-bold'}>
                                <span className={'mr-1'}>{errorMessage}</span>
                            </div>
                        }
                    </div>

                    <div className="flex justify-end items-center w-1/2">
                        <RenderFields
                            parentForm={contentDetail}
                            handleDetailChanges={handleContentDetailChanges}
                            fieldList={_.groupBy(DESIGN_CONTENT_INPUT_FIELDS, 'group')['meta']}
                            enableEdit={true}
                            noFieldLabel={true}
                            fieldClassName={'w-full'}/>
                        <button
                            className="button-green px-4 py-2 flex items-center justify-center"
                            onClick={() => {
                                generateProductDescriptions(false);
                            }}>
                            <span>Generate Descriptions</span>
                        </button>
                    </div>

                </div>
            </div>

            <div className="flex justify-start w-full">

                <div
                    className="w-1/2 meta-description-list-container rounded p-4 mt-2 overflow-y-scroll border border-gray-400 bg-white">

                    <ShowEmptyListMessage
                        dataList={designDetail?.variants}
                        dataFetchInProgress={dataFetchInProgress}
                        label={'Design Variants'}/>

                    <DesignDetailTable
                        parentProduct={parentProduct}
                        designTagList={designTagList}
                        designDetail={designDetail}/>

                    {designDetail?.variants?.length > 0 &&
                        <table className="w-full">
                            <thead className="">
                            <tr>
                                <th className="p-2 border border-gray-400 sticky top-0 bg-gray-400 text-white">S.No</th>
                                {DESIGN_VARIANT_FIELDS_LIST?.map((variantField, variantFieldIndex) => (
                                    <th key={`th-${variantFieldIndex}`}
                                        className="p-2 border border-gray-400 sticky top-0 bg-gray-400 text-white">{variantField?.label}</th>
                                ))}

                                <th className="px-2 pb-2 border border-gray-400 sticky top-0 bg-gray-400 text-white">Action</th>
                            </tr>
                            </thead>
                            <tbody className="">
                            {designDetail?.variants?.map((variant, variantFieldIndex) => (
                                <tr key={`row-${variantFieldIndex}`} className="border border-gray-400">
                                    <td className="p-2 border border-gray-400 m-auto">
                                        <div className="flex justify-center">
                                            {variantFieldIndex + 1}
                                        </div>
                                    </td>

                                    {DESIGN_VARIANT_FIELDS_LIST?.map((variantField, columnIndex) => (
                                        <React.Fragment key={`column-${variantFieldIndex}-${columnIndex}`}>
                                            <td className={`p-2 border border-gray-400 w-fit ${variantField?.cssClass}`}>
                                                {variant[variantField?.attribute] &&
                                                    <>

                                                        {variantField?.attribute !== 'status' && variant[variantField?.attribute] &&
                                                            <>{variant[variantField?.attribute]}</>
                                                        }

                                                        {variantField?.attribute === 'status' && variant[variantField?.attribute] &&
                                                            <ShowDesignVariantStatus
                                                                designVariantDetail={variant}/>
                                                        }

                                                    </>
                                                }
                                            </td>
                                        </React.Fragment>
                                    ))}

                                    <td className="w-32 p-2 border border-gray-400 m-auto text-center">
                                        <div className="flex justify-around">
                                            <CopyDataToClipboard value={variant?.label}/>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    }
                </div>

                <div
                    className="w-1/2 meta-description-list-container rounded overflow-y-scroll bg-gray-300 border border-gray-400 pl-2 pr-4 py-4 ml-4 mt-2">

                    {designDetail?.content?.contentList?.map((productContent, index) => (
                        <div key={`description-${index}`}
                             className="flex justify-start">
                            <div
                                className="flex justify-center self-center text-2xl text-gray-500 w-10">{index + 1}</div>
                            <div className="w-full m-2 p-4 border border-gray-400 bg-white rounded-xl">
                                <div className={`flex justify-start text-gray-900 font-bold`}>
                                    <div className={''}>Meta Title</div>
                                </div>
                                <div>{productContent.metaTitle}</div>
                                <div className={`mt-2 flex justify-start text-gray-900 font-bold`}>
                                    <div className={''}>Meta Description</div>
                                </div>
                                <div>{productContent.metaDescription}</div>
                                <div className={`mt-2 flex justify-start text-gray-900 font-bold`}>
                                    <div className={''}>Product Description</div>
                                </div>
                                <div>{productContent.productDescription}</div>
                                <div className={`mt-2 flex justify-start text-gray-900 font-bold`}>
                                    <div className={''}>Facebook Post</div>
                                </div>
                                <div>{productContent.facebookPost}</div>
                            </div>
                            {/*<div
                                className="flex justify-center self-center text-xl text-purple-600 w-10 mx-2">{productContent?.length}</div>*/}
                        </div>
                    ))}

                </div>

            </div>
        </>
    )
}

export default DesignSummary;
